"use client";

import { FREQUENCIES } from "@/data/calculator-data";
import { formatPrice, useCurrency } from "@/lib/currency";
import type { CalcResult, Locale } from "@/types/calculator";
import { useLocale } from "next-intl";

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-black/10 p-5 dark:border-white/10">
      <div className="text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{label}</div>
      <div className="mt-1 font-bebas text-2xl text-ash dark:text-white">{value}</div>
    </div>
  );
}

/** Right results panel: instalment, stats, distribution + lock CTA. */
export default function CalcResults({ result, downPct, freqKey, onBook }: { result: CalcResult; downPct: number; freqKey: string; onBook: () => void }) {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { code } = useCurrency();
  const freq = FREQUENCIES.find((f) => f.key === freqKey) ?? FREQUENCIES[0];

  return (
    <div className="space-y-8 border border-black/10 bg-black/[0.02] p-6 md:p-8 dark:border-white/10 dark:bg-white/[0.02]">
      <div>
        <span className="text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "القسط" : "Instalment"} · {freq.label[locale]}</span>
        <div className="font-bebas text-[clamp(3rem,6vw,5rem)] leading-none text-accent">{formatPrice(result.installment, code, locale)}</div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Stat label={ar ? "المقدم المدفوع" : "Down paid"} value={formatPrice(result.down, code, locale)} />
        <Stat label={ar ? "إجمالي المتبقي" : "Total remaining"} value={formatPrice(result.remaining, code, locale)} />
      </div>

      <div>
        <span className="mb-2 block text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "توزيع المبالغ" : "Distribution"}</span>
        <div className="flex h-4 w-full overflow-hidden border border-black/10 dark:border-white/10">
          <div className="bg-accent transition-all duration-500" style={{ width: `${downPct}%` }} />
          <div className="bg-black/15 transition-all duration-500 dark:bg-white/15" style={{ width: `${100 - downPct}%` }} />
        </div>
        <div className="mt-2 flex justify-between text-[11px] font-semibold uppercase tracking-widest">
          <span className="text-accent">{ar ? `مقدم ${downPct}%` : `Down ${downPct}%`}</span>
          <span className="text-ash/50 dark:text-white/50">{ar ? `أقساط ${100 - downPct}%` : `Instalments ${100 - downPct}%`}</span>
        </div>
      </div>

      <button onClick={onBook} data-cursor className="flex h-14 w-full items-center justify-center bg-accent text-[12px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-ash dark:hover:bg-white dark:hover:text-ash">
        {ar ? "احجز استشارة لتثبيت هذه الخطة" : "Book a consultation to lock this plan"}
      </button>
    </div>
  );
}
